import { EnumValues } from '@/services/types/EnumValues';

/** Mirrors the backend's MultiSignActionState (@JsonValue - lowercase on the wire). */
export const MultiSignActionStateEnum = {
  PENDING: 'pending',
  COMPLETED: 'completed',
  REJECTED: 'rejected',
  EXPIRED: 'expired'
} as const;
export type TMultiSignActionState = EnumValues<typeof MultiSignActionStateEnum>;

/** Mirrors the backend's MultiSignActionAuthorisationState (@JsonValue - lowercase on the wire). */
export const MultiSignActionAuthorisationStateEnum = {
  PENDING: 'pending',
  APPROVED: 'approved',
  REJECTED: 'rejected'
} as const;
export type TMultiSignActionAuthorisationState = EnumValues<
  typeof MultiSignActionAuthorisationStateEnum
>;

/** Mirrors the backend's RsMultiSignActionAuthorisation - one approver's slot on the action. */
export type TMultiSignActionAuthorisation = {
  id: string;
  created: string;
  modified: string;
  actionId: string;
  accountId: string;
  customerId: string;
  state: TMultiSignActionAuthorisationState;
  notes?: string | null;
};

/** Mirrors the backend's RsMultiSignAction. */
export type TMultiSignAction = {
  id: string;
  created: string;
  modified: string;
  ruleId: string;
  type: string;
  state: TMultiSignActionState;
  requiredApprovals: number;
  triggeredByAccountId: string;
  triggeredByCustomerId: string;
  authorisations: TMultiSignActionAuthorisation[];
  data: string;
};

/**
 * Error codes the authorise/reject endpoints can return (see ErrorResponseBody.code) when this
 * request arrives too late - someone (possibly this same customer, from another device such as
 * the mobile app) already acted since this browser last fetched the action. Both are handled the
 * same way - see reconcileAfterAuthorisationConflict in useScheduledPaymentAction, which
 * refetches the action and updates the UI instead of just showing a dead-end error.
 *
 * AUTHORISATION_NOT_PENDING: this customer's own authorisation slot is no longer `pending` - they
 * already approved/rejected it, though the action as a whole can still be PENDING if other
 * approvers are still required.
 * ACTION_NOT_PENDING: the action as a whole is no longer PENDING at all - it already reached a
 * terminal outcome (COMPLETED/REJECTED/EXPIRED), regardless of whose authorisation caused that.
 * EXECUTION_FAILED: matches the backend's `Failure(4104, "Multi sign action execution error")`
 * (MultiSignActionExecutionException). Thrown from authoriseMultiSignAction itself only for an
 * individual scheduled payment (kind !== BATCH) whose approval was also the final one required -
 * the backend runs that day's payment synchronously as part of completing the action, so a
 * same-day payment failure surfaces here rather than as a separate call.
 *
 * Confirmed against AuthoriseMultiSignActionUseCaseImpl/ExecuteMultiSignActionSendBankPaymentUseCase
 * (backend): the immediate payment attempt's specific failure reason (e.g. insufficient balance,
 * `Failure(4017, "Insufficient balance")`) IS distinguished internally for notification purposes,
 * but that specific exception is then re-thrown, caught by AuthoriseMultiSignActionUseCaseImpl's
 * own unconditional `catch (e: Exception)`, and converted to this generic EXECUTION_FAILED code
 * before the HTTP response is built - 4017 itself never actually reaches this endpoint's response.
 * useScheduledPaymentAction treats any EXECUTION_FAILED the same way regardless of cause (shows
 * paymentFailed), since the specific reason is lost by the time it gets here.
 *
 * A batch's equivalent run happens asynchronously on the backend instead (see
 * ExecuteMultiSignActionApproveScheduledBankPaymentGroupUseCase, which only flips the group's own
 * approval flag synchronously), so approving a batch never throws this either - see
 * useScheduledPaymentAction's post-approval group-status fetch for how that case is detected.
 */
export const MultiSignErrorCodeEnum = {
  AUTHORISATION_NOT_PENDING: '4100',
  ACTION_NOT_PENDING: '4103',
  EXECUTION_FAILED: '4104'
} as const;
